# Changelog

## 2026-05-25 — OpenAPI spec hardening

- **DOCS:** `/openapi.json` now exposes full request body schemas for all DTOs (previously
  paths-only). Scalar try-it console at [/reference](/reference) renders complete request
  bodies with field types, bounds, and enum values. No API behavior change.

## 2026-05-24 — Approval-rate phase 1

- **NEW:** `deviceId` (optional, string) on `POST /api/v1/transactions` and
  `POST /api/v1/transactions/card`. Populate with `window.MP_DEVICE_SESSION_ID` from the MP
  SDK on the buyer's browser. N4 forwards as `additional_info.session_id` to the provider.
  Published lift on approval: 5-15%. See [tokenization](/tokenization) for the integration snippet.

## 2026-05-22 — PIX MED auto-ingest

- **NEW:** N4 now auto-detects PIX MED reversals via orphan-refund inspection on the MP
  webhook channel. When MP creates an automatic refund for a PIX payment with no matching
  N4 row, N4 opens a `PixMedCase` (status `reversed`) and writes the ledger debit. Operator
  doesn't need to manually flag bank emails — they show up in `/admin/pix-med` automatically.
- **NEW:** Opt-in postback events `payment.pix_med_opened`, `pix_med_accepted`,
  `pix_med_rejected`, `pix_med_reversed`. Enable per store (`postback_pix_med_enabled`).

## 2026-05-21 — Sprint Disputas (chargebacks)

- **NEW:** Opt-in postback events `payment.chargeback_opened`, `chargeback_won`,
  `chargeback_lost`. Enable per store (`postback_chargeback_enabled`).
- **CRON:** `ChargebackDeadlineCron` sends ops alerts at 48h / 24h / expired thresholds.
- **NEW:** Operator endpoints to contest (`POST /admin/chargebacks/:id/dispute`) or accept
  loss (`POST /admin/chargebacks/:id/accept`) with side-panel UI under `/chargebacks`.

## 2026-05-19 — Super-admin + cross-org

- **INTERNAL:** Platform operator role (`super_admin`) can now operate across organizations
  via `OrgSelector` in the admin header. Cross-org provider account assignments preserve
  credentials inside the operating org (integrators never see provider credentials).
- **NEW:** Postback signing rotation now ships a 24h dual-secret grace period —
  `X-N4-Signature-Previous` header carries the previous signature so integrators have time
  to deploy the new secret without a verification gap. See [postbacks](/postbacks).

## 2026-05-17 — Audit hardening (pre-multi-provider)

- **FIX:** Webhook `payment.updated` repeats no longer double-count `account_usage_counters`
  (capacity score now accurate under MP's at-least-once delivery).
- **FIX:** Idempotency replay after provider-side failure preserves the failure as canonical
  response (was returning generic 500 on retry; now matches Stripe semantics).
- **FIX:** Boleto gained own daily limit bucket; no longer steals capacity from card pool.
- **FIX:** Late `payment.cancelled` webhook arriving after `paid` no longer reverses the intent.
- **FIX:** Postback resend dedupes pending retry jobs to prevent duplicate delivery.
- **FIX:** Refund TOCTOU race — concurrent refund requests on the same intent now serialize
  via `SELECT … FOR UPDATE`, preventing total-refunded > original-amount.
- **NEW:** Stuck-attempt timeout cron — attempts older than 24h with no webhook transition to
  `failed` automatically and fire `payment.failed` postback.

## 2026-05-17 — V1 papercuts batch 2

- **NEW:** `GET /admin/audit-log` viewer with cursor pagination and filters (actor, action,
  target, date range). Operator-only.
- **NEW:** `POST /admin/webhooks/:id/reprocess` — operator-initiated reprocess of stuck webhooks.
- **NEW:** `POST /admin/postbacks/:id/resend` — operator-initiated resend, using the *current*
  signing secret (rotation-safe).
- **NEW:** API key rate limit — sliding window 120 req / 60s per key on gateway endpoints;
  returns `429` with `Retry-After`.
- **NEW:** IP allowlist enforcement — when `api_keys.ip_whitelist` is set, requests from
  other IPs return `401`.

## 2026-05-16 — Sprint 9

- **NEW:** `POST/GET/PATCH /api/v1/recipients` — pre-create payout destinations with KYC review
- **NEW:** `splits[]` field on `POST /api/v1/transactions` — marketplace splits, validated +
  persisted before adapter call. All-or-nothing amount-vs-percentage; sum tolerance 1 cent
- **NEW:** Two-step auth/capture — pass `capture: false` on card transactions to get a payment
  in `authorized` state; finalize via `POST /api/v1/transactions/:id/capture` (body `{amount?}`
  for partial) or release via `POST /api/v1/transactions/:id/void`
- **STATE:** New intent `IntentStatus = 'authorized'` (transitions: `processing → authorized →
  paid | cancelled | expired`)

## 2026-05-16 — Sprint 8

- **NEW:** `POST/GET/PATCH /api/v1/customers` — pre-create customers (find-or-merge by
  `externalRef` → `email` → `document.number`); `GET /:id/transactions` for extrato
- **NEW:** `POST /api/v1/transactions` `paymentMethod=boleto` — boleto bancário with
  `customer.address` + `customer.document` required; response carries `linhaDigitavel`,
  `barcode`, `pdfUrl`, `expiresAt`
- **NEW:** `POST/GET /api/v1/transfers` — programmatic PIX-out via stored merchant balance
- **EVENTS:** Postback events `transfer.completed` and `transfer.failed` added
- **CRON:** `BoletoExpiryCron` expires unpaid boletos 6h past `expiresAt` and fires
  `payment.expired` postback

## 2026-05-15 — Sprint 7

- **BREAKING (additive):** `POST /api/v1/transactions` unified — paymentMethod-driven dispatch
  (PIX, credit_card, debit_card; boleto stub). Legacy `POST /api/v1/transactions/card` kept
  with `Deprecation`, `Sunset` (2028-01-01), `Link` rel=successor-version headers
- **NEW:** `GET /api/v1/transactions` list endpoint with cursor + filters
- **NEW:** `GET /api/v1/transactions/:id` enriched detail (customer, card, pix, items,
  refunds, fee.netAmount)
- **NEW:** `card.token` field accepts pre-tokenized cards from `@n4/checkout-js` SDK
- **NEW:** `payment.reminder_due` postback event (default-OFF until store opts in via
  `pix_reminder_minutes` or `boleto_reminder_hours`)
- **NEW:** SDK `@n4/checkout-js` for browser-side card tokenization (PCI SAQ-A)
- **STATUS:** `partially_refunded` and `in_protest` surface distinctly (was collapsed to `paid`)
- **STATUS:** Test mode (`api_keys.mode='test'` × `provider_accounts.environment='sandbox'`)
  hard-filtered at routing — cross-wire impossible
- **DIALECT:** Per-store `postback_status_dialect='elysium'` translates payloads to PrimeCash
  spec (10-value status enum + field renames + envelope wrap)
- **RELAXED:** `User-Agent` header now warn-only (was 401)

## Earlier sprints

Sprints 0-6 covered scaffold, auth + multi-store, payment core (PIX + card + routing + MP),
async pipeline (webhooks + ledger + postbacks), admin UI, operations (refunds + withdrawals
+ reports + disputes-passive), and production hardening (observability, structured logging,
crons). See [github.com/HaveFunnels/n4](https://github.com/HaveFunnels/n4) commit history.
